import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import LanguageSelector from '../components/LanguageSelector';
import { 
  ChevronDown, 
  Search, 
  FileSpreadsheet, 
  Calculator, 
  Zap, 
  Globe, 
  FileText, 
  Clock,
  CreditCard,
  Building2,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Play,
  Home
} from 'lucide-react';

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // FAQ Categories with icons
  const categories = [
    { id: 'all', label: t('faq.allQuestions'), icon: null },
    { id: 'spreadsheet', label: t('faq.categories.spreadsheet'), icon: FileSpreadsheet },
    { id: 'financial', label: t('faq.categories.financial'), icon: Calculator },
    { id: 'simplicity', label: t('faq.categories.simplicity'), icon: Zap },
    { id: 'features', label: t('faq.categories.features'), icon: FileText },
    { id: 'international', label: t('faq.categories.international'), icon: Globe },
    { id: 'pricing', label: t('faq.categories.pricing'), icon: CreditCard },
  ];

  // Comprehensive FAQ data with SEO keywords
  const faqData = [
    // Category A: Escaping Spreadsheet Chaos
    {
      category: 'spreadsheet',
      question: t('faq.questions.excelBetter.q'),
      answer: t('faq.questions.excelBetter.a'),
      keywords: ['tour operator software', 'DMC management software', 'travel business operating system']
    },
    {
      category: 'spreadsheet',
      question: t('faq.questions.automateWorkflows.q'),
      answer: t('faq.questions.automateWorkflows.a'),
      keywords: ['inbound tourism automation', 'automate tour operator workflows']
    },
    {
      category: 'spreadsheet',
      question: t('faq.questions.stopSpreadsheets.q'),
      answer: t('faq.questions.stopSpreadsheets.a'),
      keywords: ['tour booking system', 'cloud-based operating system for travel business']
    },
    
    // Category B: Financial Precision
    {
      category: 'financial',
      question: t('faq.questions.trackProfits.q'),
      answer: t('faq.questions.trackProfits.a'),
      keywords: ['tour package costing tool', 'profit monitoring', 'gross margin tracking']
    },
    {
      category: 'financial',
      question: t('faq.questions.bottomUpCosting.q'),
      answer: t('faq.questions.bottomUpCosting.a'),
      keywords: ['bottom-up tour costing tool', 'itemized cost elements for travel packages']
    },
    {
      category: 'financial',
      question: t('faq.questions.contingencyMargins.q'),
      answer: t('faq.questions.contingencyMargins.a'),
      keywords: ['track tour package contingencies and margins', 'cost control']
    },
    {
      category: 'financial',
      question: t('faq.questions.vendorPayments.q'),
      answer: t('faq.questions.vendorPayments.a'),
      keywords: ['manage vendor payments for tour packages', 'travel CRM and invoicing']
    },
    
    // Category C: Simplicity & Speed
    {
      category: 'simplicity',
      question: t('faq.questions.technicalTraining.q'),
      answer: t('faq.questions.technicalTraining.a'),
      keywords: ['simple tour operator software for small teams', 'simple interface']
    },
    {
      category: 'simplicity',
      question: t('faq.questions.simpleInbound.q'),
      answer: t('faq.questions.simpleInbound.a'),
      keywords: ['best simple software for inbound travel agencies', 'execution efficiency']
    },
    {
      category: 'simplicity',
      question: t('faq.questions.getStarted.q'),
      answer: t('faq.questions.getStarted.a'),
      keywords: ['tour package execution tracking', 'real-time analytics']
    },
    
    // Features & Capabilities
    {
      category: 'features',
      question: t('faq.questions.multiDayItineraries.q'),
      answer: t('faq.questions.multiDayItineraries.a'),
      keywords: ['itinerary builder', 'booking management']
    },
    {
      category: 'features',
      question: t('faq.questions.documentVault.q'),
      answer: t('faq.questions.documentVault.a'),
      keywords: ['digital document vault for tour operators', 'traffic light compliance for travel documents']
    },
    {
      category: 'features',
      question: t('faq.questions.bestToolsInbound.q'),
      answer: t('faq.questions.bestToolsInbound.a'),
      keywords: ['best tools for inbound tour operators', 'dispatch']
    },
    {
      category: 'features',
      question: t('faq.questions.serviceProviderPayments.q'),
      answer: t('faq.questions.serviceProviderPayments.a'),
      keywords: ['track service provider payments', 'account payable']
    },
    
    // International & Multi-currency
    {
      category: 'international',
      question: t('faq.questions.multiCurrency.q'),
      answer: t('faq.questions.multiCurrency.a'),
      keywords: ['multi-currency', 'international bookings', 'cross-border']
    },
    {
      category: 'international',
      question: t('faq.questions.dmcMultiCurrency.q'),
      answer: t('faq.questions.dmcMultiCurrency.a'),
      keywords: ['best software for DMCs to manage international multi-currency bookings', 'DMC system']
    },
    
    // Pricing & Trial
    {
      category: 'pricing',
      question: t('faq.questions.freeTrial.q'),
      answer: t('faq.questions.freeTrial.a'),
      keywords: ['free trial', 'pricing']
    },
    {
      category: 'pricing',
      question: t('faq.questions.pricingPlans.q'),
      answer: t('faq.questions.pricingPlans.a'),
      keywords: ['tour operator software pricing', 'subscription']
    },
    {
      category: 'pricing',
      question: t('faq.questions.teamMembers.q'),
      answer: t('faq.questions.teamMembers.a'),
      keywords: ['team collaboration', 'multi-user']
    },
  ];

  // Filter FAQs based on search and category
  const filteredFaqs = faqData.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Get current language for SEO
  const currentLang = i18n.language || 'en';
  
  // Multilingual SEO meta content
  const seoContent = {
    en: {
      title: "FAQ - Tour Operator Software Questions | TourPilot.biz",
      description: "Frequently asked questions about TourPilot.biz - the best DMC management software for tour operators. Learn about tour package costing, profit tracking, and automation.",
      keywords: "tour operator software, DMC management software, travel ERP, tour booking system, inbound tourism automation, tour package costing tool, travel business operating system"
    },
    fi: {
      title: "UKK - Matkanjärjestäjäohjelmiston kysymykset | TourPilot.biz",
      description: "Usein kysytyt kysymykset TourPilot.biz:stä - paras DMC-hallintaohjelmisto matkanjärjestäjille. Opi matkapakettien kustannuslaskennasta, voitonseurannasta ja automaatiosta.",
      keywords: "matkanjärjestäjäohjelmisto, DMC-hallintaohjelmisto, matka-ERP, matkavarausjärjestelmä, sisäänpäin suuntautuvan matkailun automaatio, matkapakettien kustannuslaskentatyökalu"
    },
    sv: {
      title: "FAQ - Turoperatörsprogramvara frågor | TourPilot.biz",
      description: "Vanliga frågor om TourPilot.biz - den bästa DMC-hanteringsprogramvaran för turoperatörer. Lär dig om turpaketkostnadsberäkning, vinstspårning och automation.",
      keywords: "turoperatörsprogramvara, DMC-hanteringsprogramvara, rese-ERP, turbokningssystem, inkommande turismautomation, turpaketkostnadsverktyg"
    }
  };

  const currentSeo = seoContent[currentLang] || seoContent.en;

  // Generate JSON-LD Schema for FAQ Rich Results (uses translated content)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "inLanguage": currentLang,
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Organization schema for better AI search visibility
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "TourPilot.biz",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": currentSeo.description,
    "offers": {
      "@type": "Offer",
      "price": "9.99",
      "priceCurrency": "EUR"
    }
  };

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <html lang={currentLang} />
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <meta name="keywords" content={currentSeo.keywords} />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={currentLang === 'fi' ? 'fi_FI' : currentLang === 'sv' ? 'sv_SE' : 'en_US'} />
        <link rel="alternate" hrefLang="en" href="https://tourpilot.biz/faq" />
        <link rel="alternate" hrefLang="fi" href="https://tourpilot.biz/faq?lng=fi" />
        <link rel="alternate" hrefLang="sv" href="https://tourpilot.biz/faq?lng=sv" />
        <link rel="canonical" href={`https://tourpilot.biz/faq${currentLang !== 'en' ? `?lng=${currentLang}` : ''}`} />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Top Navigation Bar */}
        <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                <ArrowLeft className="w-4 h-4 text-muted-foreground" />
                <div className="flex flex-col items-start">
                  <img 
                    src="/tourpilot-logo.png" 
                    alt="TourPilot.biz" 
                    className="h-11 md:h-14 max-w-[220px] object-contain"
                  />
                  <span className="text-xs text-muted-foreground mt-1 hidden sm:block whitespace-nowrap">
                    {t('faq.tagline')}
                  </span>
                </div>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector className="border-muted" />
              <Button asChild size="sm" className="hidden sm:inline-flex">
                <Link to="/register">{t('common.getStarted')}</Link>
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative py-16 md:py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <Badge variant="secondary" className="mb-4">
                {t('faq.badge')}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-heading font-extrabold text-foreground mb-6">
                {t('faq.title')}
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                {t('faq.subtitle')}
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('faq.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg rounded-full border-2 focus:border-primary"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <section className="py-8 border-b bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <Button
                    key={cat.id}
                    variant={activeCategory === cat.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveCategory(cat.id)}
                    className="rounded-full"
                  >
                    {Icon && <Icon className="w-4 h-4 mr-2" />}
                    {cat.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ List */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">{t('faq.noResults')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFaqs.map((faq, index) => (
                  <Card 
                    key={index}
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'
                    }`}
                  >
                    <button
                      className="w-full text-left p-6 flex items-start justify-between gap-4"
                      onClick={() => toggleFaq(index)}
                      aria-expanded={openIndex === index}
                    >
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground pr-4">
                          {faq.question}
                        </h3>
                        {openIndex !== index && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                            {faq.answer.substring(0, 100)}...
                          </p>
                        )}
                      </div>
                      <ChevronDown 
                        className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-300 ${
                          openIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openIndex === index && (
                      <CardContent className="pt-0 pb-6 px-6">
                        <div className="border-t pt-4">
                          <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                            {faq.answer}
                          </p>
                          {/* Keywords for SEO (hidden visually) */}
                          <div className="sr-only">
                            {faq.keywords.join(', ')}
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Video CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <a 
                href="https://youtu.be/YDK4StPd2Cs" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group block"
              >
                <div className="relative bg-gradient-to-r from-primary via-purple-600 to-secondary rounded-2xl p-1 shadow-xl hover:shadow-primary/25 transition-all duration-300 hover:scale-[1.02]">
                  <div className="bg-background rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6">
                    <div className="relative flex-shrink-0">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Play className="w-10 h-10 text-white fill-white ml-1" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                        <span className="text-white text-xs font-bold">9</span>
                      </div>
                    </div>
                    <div className="text-center md:text-left flex-1">
                      <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-2">
                        {t('faq.videoTitle')}
                      </h3>
                      <p className="text-muted-foreground">
                        {t('faq.videoDesc')}
                      </p>
                    </div>
                    <ArrowRight className="w-6 h-6 text-primary group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-secondary">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              {t('faq.ctaTitle')}
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              {t('faq.ctaDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="rounded-full">
                <Link to="/register">
                  {t('faq.startFreeTrial')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full bg-white/10 text-white border-white/30 hover:bg-white/20">
                <Link to="/pricing">
                  {t('faq.viewPricing')}
                </Link>
              </Button>
            </div>
            
            {/* Trust indicators */}
            <div className="mt-12 flex flex-wrap justify-center gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>{t('faq.trust.noCard')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>{t('faq.trust.twoMonths')}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>{t('faq.trust.cancelAnytime')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Still Have Questions */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
              {t('faq.stillQuestions')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('faq.contactUs')}
            </p>
            <Button asChild variant="outline" size="lg">
              <a href="mailto:support@tourpilot.biz">
                {t('faq.emailSupport')}
              </a>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;
